// react
import { useContext } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

// context
import { ContextProject } from '../Project.context';
import { ContextUser } from '../../../../App.context';

// mui
import { ChipProps, Container, Grid, Typography } from '@mui/material';

// cards
import { CardAction, CardActionProps } from '../../../cards/CardAction/CardAction';

// dialogs
import { DialogDownloadReport } from '../../../dialogs/DialogDownloadReport/DialogDownloadReport';

// utils
import { getReports } from '../../../../utils/reports';

// icons
import { DownloadOutlined as IconDownload } from '@mui/icons-material';

export const Reports = () => {
	// hooks
	const { t } = useTranslation();

	// context
	const { project } = useContext(ContextProject);
	const { user } = useContext(ContextUser);

	// state
	const [searchParams, setSearchParams] = useSearchParams();

	// params
	const paramDialog = searchParams.get('dialog');

	const PaperProps: CardActionProps['PaperProps'] = {
		sx: {
			height: '100%',
			minHeight: {
				xs: 200,
				md: 300,
			},
		},
	};

	const ButtonProps: CardActionProps['ButtonProps'] = {
		sx: (theme) => {
			return {
				border: 'none',
				height: '100%',
				padding: theme.spacing(2),
				color: 'black',
				'&:hover': {
					border: 'none',
				},
			};
		},
	};

	const reports = getReports({ t, user });

	const cards: CardActionProps[] = [];

	// for each report
	for (let i = 0; i < reports.length; i++) {
		const report = reports[i];

		const cardActionProps: CardActionProps = {
			content: report.content,
			hasBreakLine: false,
			IconCircleProps: report.IconCircleProps,
			GridProps: report.GridProps,
			PaperProps,
			title: {
				...report.title,
				component: 'h3',
				variant: 'h2',
			},
			titleGridProps: {
				alignItems: 'center',
			},
		};

		const action = {
			children: t('downloadReport'),
		};

		let reportChipProps: ChipProps | null = null;

		const reportButtonProps: CardActionProps['ButtonProps'] = {
			...ButtonProps,
			disabled: report.disabled,
		};

		// report button props
		if (report.type === 'FUND_FLOW') {
			action.children = t('seeReport');
			reportButtonProps.component = RouterLink;
			reportButtonProps.to = `/project/${project?.idWbg}/reports/flow`;
		} else if (report.type === 'SOE') {
			action.children = t('createReport');
			reportButtonProps.component = RouterLink;
			reportButtonProps.to = `/project/${project?.idWbg}/reports/soe`;
		} else if (report.type === 'IFR') {
			action.children = t('createReport');
			reportButtonProps.component = RouterLink;
			reportButtonProps.to = `/project/${project?.idWbg}/reports/financial`;
		} else {
			// other reports
			cardActionProps.icon = <IconDownload />;
			reportButtonProps.onClick = () => {
				searchParams.set('dialog', 'downloadReport');
				searchParams.set('dialogReport', report.type);
				setSearchParams(searchParams);
			};
		}

		if (report.disabled) {
			reportChipProps = {
				label: t('comingSoon'),
				size: 'small',
				variant: 'twoTone',
			};
		}

		cards.push({
			...cardActionProps,
			action,
			ButtonProps: reportButtonProps,
			ChipProps: reportChipProps,
		});
	}

	return (
		<>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Grid item xs={12}>
						<Container maxWidth={false}>
							<Typography variant="h2">{t('reports')}</Typography>
						</Container>
					</Grid>
				</Grid>
				<Grid item xs={12}>
					<Container maxWidth={false}>
						<Grid container spacing={2}>
							{cards.map((card, i) => {
								const { GridProps, ...cardProps } = card;
								return (
									<Grid item key={`report-${i}`} xs={12} md={6} {...GridProps}>
										<CardAction {...cardProps} />
									</Grid>
								);
							})}
						</Grid>
					</Container>
				</Grid>
			</Grid>
			<DialogDownloadReport
				isOpen={paramDialog === 'downloadReport'}
				onClose={() => {
					searchParams.delete('dialog');
					searchParams.delete('dialogReport');
					setSearchParams(searchParams);
				}}
			/>
		</>
	);
};
